<template>
  <v-container class="pa-0" :class="{ 'export-embedded': embedded }" fluid>
    <v-container v-if="!embedded" class="pt-6 pb-6 px-4" fluid>
      <v-row align="center" class="ma-0">
        <v-col cols="auto" class="py-0 pr-2">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                label="Monat"
                v-model="monthFormatted"
                readonly
                outlined
                dense
                hide-details
                style="width: 150px;"
                color="#FF6600"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="month"
              no-title
              scrollable
              type="month"
              color="#FF6600"
              @input="menu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="auto" class="py-0 pr-2">
          <v-select
            v-model="selectedLineIds"
            :items="lines"
            :item-text="line => `${line.line_id} ${line.description}`"
            item-value="line_id"
            label="Linie"
            multiple
            outlined
            dense
            hide-details
            style="width: 250px;"
            color="#FF6600"
            item-color="primary"
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @click="toggleAllLines">
                <v-list-item-action>
                  <v-icon :color="selectedLineIds.length > 0 ? 'primary' : ''">{{ selectAllIcon }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Alle Linien</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="allLinesSelected && index === 0" class="text-truncate">Alle Linien</span>
              <span v-else-if="!allLinesSelected && index === 0" class="text-truncate">{{ item.line_id }}</span>
              <span
                v-else-if="!allLinesSelected && index === 1"
                class="grey--text text-caption ml-1"
              >(+{{ selectedLineIds.length - 1 }})</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="auto" class="py-0">
          <v-text-field
            v-model="searchText"
            label="Suche"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="width: 250px;"
            color="#FF6600"
            @input="onSearchInput"
          ></v-text-field>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto" class="py-0 pr-1">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="collapseAllClick"
          >
            <v-icon small color="grey darken-2">{{ expanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto" class="py-0 pr-1">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="refreshDataGrid"
          >
            <v-icon small color="grey darken-2">mdi-refresh</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto" class="py-0">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="exportToExcel"
          >
            <v-icon small color="grey darken-2">mdi-download</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-divider v-if="!embedded"></v-divider>
    <dx-data-grid
      :ref="gridRefName"
      :data-source="datasource"
      :columns="columns"
      :summary="summary"
      :show-borders="false"
      :show-column-lines="false"
      :column-auto-width="true"
      :allow-column-reordering="!gridTouch"
      :allow-column-resizing="!gridTouch"
      :hover-state-enabled="!gridTouch"
      :show-row-lines="true"
      :height="embedded ? '100%' : undefined"
      :error-row-enabled="!embedded"
      @cell-prepared="cellPrepared"
      @toolbar-preparing="onToolbarPreparing($event)"
      :scrolling="scrolling"
    >
      <dx-group-panel :visible="false" />

      <dx-grouping :auto-expand-all="expanded" expandMode="rowClick" />
      <dx-paging :page-size="defaultPageSize" />
      <dx-pager
        :visible="!embedded"
        :show-page-size-selector="true"
        :allowed-page-sizes="pageSizes"
        :show-info="true"
        :info-text="'Seite {0} von {1} ({2} Verkäufe)'"
      />
      <dx-filter-row :visible="!embedded" />
      <dx-load-panel :enabled="!embedded" />
      <dx-export
        :enabled="false"
        :allow-export-selected-data="true"
        file-name="NVM"
        :texts="{
          exportAll: 'Alles herunterladen',
          exportSelectedRows: 'Auswahl herunterladen',
          exportTo: 'Excel Download',
        }"
      />

      <dx-selection :mode="embedded ? 'none' : 'multiple'" />
    </dx-data-grid>
  </v-container>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxFilterRow,
  DxPager,
  DxExport,
  DxGrouping,
  DxGroupPanel,
  DxSelection,
  DxLoadPanel,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import moment from "moment";
import DataGridMixin from "@/mixins/DataGridMixin";

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxFilterRow,
    DxPager,
    DxExport,
    DxGrouping,
    DxGroupPanel,
    DxSelection,
    DxLoadPanel,
  },
  mixins: [DataGridMixin],
  props: {
    // eingebettet in die Seite Exporte: ohne eigene Leiste, Filterzeile, Pager, Ladeanzeige und Fehlerzeile
    embedded: { type: Boolean, default: false },
    // Berichtsmonat 'YYYY-MM' der Seite Exporte
    reportMonth: { type: String, default: null },
  },
  computed: {
    monthFormatted() {
      return moment(this.month).format("MM.YYYY");
    },
    allLinesSelected() {
      return this.lines.length > 0 && this.selectedLineIds.length === this.lines.length;
    },
    selectAllIcon() {
      if (this.allLinesSelected) return "mdi-checkbox-marked";
      if (this.selectedLineIds.length > 0) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    lineFilterParam() {
      // no filter when all (or no) lines are selected
      if (this.allLinesSelected || this.selectedLineIds.length === 0) return "";
      return `&line_ids=${this.selectedLineIds.join(",")}`;
    },
    dataUrl() {
      return `${this.$store.state.baseUrl}ticketsale?customer_id=${
        this.$store.state.customerId
      }&sale_month=${this.month.substring(5, 7)}&sale_year=${this.month.substring(
        0,
        4
      )}&option=nvmExport${this.lineFilterParam}`;
    },
  },
  watch: {
    dataUrl: function () {
      this.refreshDataGrid();
    },
    reportMonth(value) {
      if (value && value !== this.month) {
        this.month = value;
      }
    },
  },
  data() {
    return {
      month: this.reportMonth || new Date().toISOString().substr(0, 7),
      menu: false,
      expanded: false,
      searchText: "",
      lines: [],
      selectedLineIds: [],
      groupSelection: null,
      gridRefName: "monthlyClosingDataGrid",
      columns: [],
      datasource: new CustomStore({
        load: () => {
          return axios.get(this.dataUrl).then(response => {
            const data = response.data || [];
            if (data.length > 0 && this.columns.length === 0) {
              this.generateColumns(data[0]);
            }
            return data;
          }).catch(() => []);
        },
      }),
    };
  },
  mounted() {
    this.readLines();
  },
  methods: {
    onToolbarPreparing() {
      // Buttons are now in the filter row, no toolbar buttons needed
    },
    async readLines() {
      try {
        const response = await axios.get(
          `${this.$store.state.baseUrl}line?customer_id=${this.$store.state.customerId}`
        );
        this.lines = response.data || [];
        // default: all lines selected -> no active filter
        this.selectedLineIds = this.lines.map(line => line.line_id);
      } catch (error) {
        this.lines = [];
        this.selectedLineIds = [];
      }
    },
    toggleAllLines() {
      if (this.allLinesSelected) {
        this.selectedLineIds = [];
      } else {
        this.selectedLineIds = this.lines.map(line => line.line_id);
      }
    },
    refreshDataGrid() {
      const grid = this.$refs[this.gridRefName];
      if (!grid || !grid.instance) {
        return;
      }
      this.columns = [];
      grid.instance.refresh();
    },
    onSearchInput() {
      if (this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance) {
        this.$refs[this.gridRefName].instance.searchByText(this.searchText || '');
      }
    },
    collapseAllClick() {
      this.expanded = !this.expanded;
    },
    exportToExcel() {
      if (this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance) {
        this.$refs[this.gridRefName].instance.exportToExcel(false);
      }
    },
    generateColumns(sampleData) {
      this.columns = Object.keys(sampleData).map(key => ({
        dataField: key,
        caption: key,
      }));
    },
  },
};
</script>

<style scoped>
/* eingebettet (Exporte): Flex-Spalte mit voller Höhe, das Grid füllt sie */
.export-embedded {
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: left;
}
</style>
